import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { IncomingIngestorWebhookAttributes } from '@loyalty/shared-common/ingestor-api-client';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';

export interface AccountIncomingWebhooksContext {
  accountWebhooks: IncomingIngestorWebhookAttributes[];
  isLoading: IsLoading;
  error: any;
}

export const AccountIncomingWebhooksContext = createContext<AccountIncomingWebhooksContext>({
  accountWebhooks: [],
  isLoading: () => false,
  error: undefined,
});

export function useAccountIncomingWebhooks() {
  return useContext(AccountIncomingWebhooksContext);
}

export function AccountIncomingWebhooksProvider({ children }: { children: ReactNode }) {
  const { currentAccount } = useAccounts();
  const { managerApi } = useApi();
  const [error, setError] = useState<any>();
  const [accountWebhooks, setAccountWebhooks] = useState<IncomingIngestorWebhookAttributes[]>([]);

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const getAccountWebhooks = useCallback(async () => {
    addLoader('getAccountWebhooks');
    try {
      const webhooks = await managerApi.getAccountIncomingWebhooks(currentAccount.id);
      setAccountWebhooks(webhooks);
    } catch (error) {
      setError(error);
    } finally {
      removeLoader('getAccountWebhooks');
    }
  }, [addLoader, removeLoader, managerApi, currentAccount]);

  useEffect(() => {
    getAccountWebhooks();
  }, [getAccountWebhooks]);

  const context = {
    accountWebhooks,
    error,
    isLoading,
  };

  return (
    <AccountIncomingWebhooksContext.Provider value={context}>
      {children}
    </AccountIncomingWebhooksContext.Provider>
  );
}
